import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  get,
  post
} from '../utils/api';

import {
  useAuth
} from '../context/AuthContext';

const colours = {
  brown: '#3B2A21',
  green: '#556B2F',
  cream: '#F2EFE7',
  gold: '#A8893D',
  red: '#8F2E24',
  border: '#E8E0D4',
  muted: '#777'
};

const statusStyle = {
  pending_generation: {
    background: '#F3F4F6',
    color: '#4B5563'
  },
  ready: {
    background: '#FFF3CD',
    color: '#7A5200'
  },
  sent: {
    background: '#DBEAFE',
    color: '#1E40AF'
  },
  viewed: {
    background: '#EDE9FE',
    color: '#5B21B6'
  },
  accepted: {
    background: '#DCFCE7',
    color: '#166534'
  },
  declined: {
    background: '#FEE2E2',
    color: '#991B1B'
  },
  expired: {
    background: '#F3F4F6',
    color: '#4B5563'
  },
  revoked: {
    background: '#FEE2E2',
    color: '#991B1B'
  },
  superseded: {
    background: '#F3F4F6',
    color: '#4B5563'
  }
};

function recordsFrom(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.users)) {
    return value.users;
  }

  return [];
}

function readableStatus(value) {
  return String(value || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, character =>
      character.toUpperCase()
    );
}

function formatDate(value) {
  if (!value) {
    return 'Not recorded';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString(
    'en-NG',
    {
      dateStyle: 'medium',
      timeStyle: 'short'
    }
  );
}

function StatusBadge({
  status
}) {
  const style =
    statusStyle[status] ||
    statusStyle.pending_generation;

  return (
    <span
      style={{
        ...style,
        display: 'inline-block',
        padding: '4px 9px',
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 700,
        whiteSpace: 'nowrap'
      }}
    >
      {readableStatus(status)}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  colour
}) {
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${colours.border}`,
        borderRadius: 10,
        padding: 14
      }}
    >
      <div
        style={{
          color: colours.muted,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '.06em',
          textTransform: 'uppercase'
        }}
      >
        {label}
      </div>

      <div
        style={{
          color: colour,
          fontSize: 24,
          fontWeight: 700,
          marginTop: 5
        }}
      >
        {value || 0}
      </div>
    </div>
  );
}

export default function Documents() {
  const {
    user
  } = useAuth();

  const [
    documents,
    setDocuments
  ] = useState([]);

  const [
    summary,
    setSummary
  ] = useState({});

  const [
    templates,
    setTemplates
  ] = useState([]);

  const [
    staff,
    setStaff
  ] = useState([]);

  const [
    statusFilter,
    setStatusFilter
  ] = useState('');

  const [
    selectedStaff,
    setSelectedStaff
  ] = useState('');

  const [
    selectedTemplate,
    setSelectedTemplate
  ] = useState('');

  const [
    hrFields,
    setHrFields
  ] = useState({});

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    working,
    setWorking
  ] = useState('');

  const [
    error,
    setError
  ] = useState('');

  const [
    notice,
    setNotice
  ] = useState('');

  const [
    selectedDocument,
    setSelectedDocument
  ] = useState(null);

  const canAuthorize =
    user?.id ===
    'f2d0a78e-b9a6-4c0c-80df-dacb3493ae78';

  const activeStaff = useMemo(
    () =>
      staff
        .filter(record =>
          record?.is_active !== false &&
          record?.employment_status !==
            'terminated'
        )
        .sort((left, right) =>
          String(left.name || '')
            .localeCompare(
              String(right.name || '')
            )
        ),
    [staff]
  );

  const eligibleTemplates = useMemo(
    () =>
      templates
        .filter(template =>
          [
            'staff',
            'prospect_or_staff'
          ].includes(
            template.recipient_scope
          ) &&
          ![
            'internal',
            'offboarding'
          ].includes(
            template.lifecycle_stage
          )
        )
        .sort((left, right) =>
          String(left.title || '')
            .localeCompare(
              String(right.title || '')
            )
        ),
    [templates]
  );

  const selectedTemplateRecord = useMemo(
    () =>
      eligibleTemplates.find(
        template =>
          template.id === selectedTemplate
      ) || null,
    [
      eligibleTemplates,
      selectedTemplate
    ]
  );

  const selectedInputFields = useMemo(
    () =>
      Array.isArray(
        selectedTemplateRecord?.input_fields
      )
        ? selectedTemplateRecord.input_fields
        : [],
    [selectedTemplateRecord]
  );

  useEffect(() => {
    const defaults = {};

    selectedInputFields.forEach(field => {
      defaults[field.field_key] =
        field.default_value || '';
    });

    setHrFields(defaults);
  }, [
    selectedTemplate,
    selectedInputFields
  ]);

  const loadQueue = async (
    filter = statusFilter
  ) => {
    const suffix = filter
      ? `?status=${encodeURIComponent(filter)}`
      : '';

    const result = await get(
      `/documents/queue${suffix}`
    );

    setDocuments(
      recordsFrom(result)
    );

    setSummary(
      result?.summary || {}
    );
  };

  const loadFoundation = async () => {
    setLoading(true);
    setError('');

    try {
      const [
        queueResult,
        templateResult,
        staffResult
      ] = await Promise.all([
        get('/documents/queue'),
        get(
          '/documents/templates?recipient_type=staff'
        ),
        get('/users')
      ]);

      setDocuments(
        recordsFrom(queueResult)
      );

      setSummary(
        queueResult?.summary || {}
      );

      setTemplates(
        recordsFrom(templateResult)
      );

      setStaff(
        recordsFrom(staffResult)
      );
    } catch (loadError) {
      setError(
        loadError.message ||
        'Documents workspace could not be loaded'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFoundation();
  }, []);

  const changeFilter = async event => {
    const value = event.target.value;

    setStatusFilter(value);
    setLoading(true);
    setError('');

    try {
      await loadQueue(value);
    } catch (filterError) {
      setError(
        filterError.message ||
        'Document queue could not be filtered'
      );
    } finally {
      setLoading(false);
    }
  };

  const prepareDocument = async event => {
    event.preventDefault();

    if (
      !selectedStaff ||
      !selectedTemplate
    ) {
      setError(
        'Select both a steward and a document template'
      );

      return;
    }

    const staffRecord =
      activeStaff.find(
        record =>
          record.id === selectedStaff
      );

    const template =
      eligibleTemplates.find(
        record =>
          record.id === selectedTemplate
      );

    const missingFields =
      selectedInputFields.filter(field =>
        field.is_required &&
        !String(
          hrFields[field.field_key] || ''
        ).trim()
      );

    if (missingFields.length > 0) {
      setError(
        'Complete the required document information: ' +
        missingFields
          .map(field => field.field_label)
          .join(', ')
      );

      return;
    }

    const confirmed =
      window.confirm(
        `Prepare ${template?.title || 'this document'} for ${staffRecord?.name || 'the selected steward'}?\n\nThis creates a ready document but does not send it.`
      );

    if (!confirmed) {
      return;
    }

    setWorking('prepare');
    setError('');
    setNotice('');

    try {
      const result = await post(
        '/documents/prepare',
        {
          recipient_type: 'staff',
          recipient_id: selectedStaff,
          template_id: selectedTemplate,
          hr_fields: hrFields
        }
      );

      setNotice(
        result?.message ||
        'Document prepared for authorization'
      );

      setSelectedTemplate('');
      setHrFields({});

      await loadQueue('');
      setStatusFilter('');
    } catch (prepareError) {
      setError(
        prepareError.message ||
        'Document preparation failed'
      );
    } finally {
      setWorking('');
    }
  };

  const authorizeDocument =
    async record => {
      const confirmed =
        window.confirm(
          `Authorize the company signature and send ${record.template_title} to ${record.recipient_name} at ${record.recipient_email}?\n\nThis action issues the document and sends the secure response link.`
        );

      if (!confirmed) {
        return;
      }

      setWorking(record.id);
      setError('');
      setNotice('');

      try {
        const result = await post(
          `/documents/${record.id}/authorize-send`,
          {
            confirm_company_signature:
              true
          }
        );

        setNotice(
          result?.message ||
          'Document authorized and sent'
        );

        await loadQueue(statusFilter);
      } catch (authorizationError) {
        setError(
          authorizationError.message ||
          'Document authorization failed'
        );
      } finally {
        setWorking('');
      }
    };

  const revokeDocument =
    async record => {
      const reason = window.prompt(
        `Enter the reason for revoking ${record.document_ref}:`
      );

      if (
        reason === null
      ) {
        return;
      }

      if (
        reason.trim().length < 5
      ) {
        setError(
          'A clear revocation reason of at least five characters is required'
        );

        return;
      }

      const confirmed =
        window.confirm(
          'Confirm revocation. The recipient link will stop working.'
        );

      if (!confirmed) {
        return;
      }

      setWorking(record.id);
      setError('');
      setNotice('');

      try {
        const result = await post(
          `/documents/${record.id}/revoke`,
          {
            reason: reason.trim()
          }
        );

        setNotice(
          result?.message ||
          'Document revoked'
        );

        await loadQueue(statusFilter);
      } catch (revokeError) {
        setError(
          revokeError.message ||
          'Document revocation failed'
        );
      } finally {
        setWorking('');
      }
    };

  const openDocument =
    async record => {
      setWorking(record.id);
      setError('');

      try {
        const result = await get(
          `/documents/${record.id}`
        );

        setSelectedDocument(
          result?.data || result
        );
      } catch (detailError) {
        setError(
          detailError.message ||
          'Document details could not be loaded'
        );
      } finally {
        setWorking('');
      }
    };

  const canRevoke = record =>
    canAuthorize &&
    [
      'ready',
      'sent',
      'viewed'
    ].includes(record.status);

  if (loading && documents.length === 0) {
    return (
      <div className="spinner" />
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'flex-start',
          gap: 15,
          flexWrap: 'wrap',
          marginBottom: 20
        }}
      >
        <div>
          <h2
            style={{
              fontFamily:
                'Cormorant Garamond, serif',
              color: colours.brown,
              fontSize: '1.8rem',
              margin: '0 0 4px'
            }}
          >
            Employment Documents
          </h2>

          <p
            style={{
              color: colours.muted,
              fontSize: 13,
              margin: 0
            }}
          >
            Prepare, authorize, deliver and
            track protected steward documents.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={loadFoundation}
          disabled={loading}
        >
          {loading
            ? 'Refreshing...'
            : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error mb-md">
          {error}
        </div>
      )}

      {notice && (
        <div
          className="alert mb-md"
          style={{
            background: '#DCFCE7',
            border: '1px solid #86EFAC',
            color: '#166534'
          }}
        >
          {notice}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit,minmax(130px,1fr))',
          gap: 10,
          marginBottom: 20
        }}
      >
        <SummaryCard
          label="Ready for Doris"
          value={summary.ready}
          colour={colours.gold}
        />

        <SummaryCard
          label="Sent"
          value={summary.sent}
          colour="#1E40AF"
        />

        <SummaryCard
          label="Viewed"
          value={summary.viewed}
          colour="#5B21B6"
        />

        <SummaryCard
          label="Accepted"
          value={summary.accepted}
          colour={colours.green}
        />

        <SummaryCard
          label="Declined"
          value={summary.declined}
          colour={colours.red}
        />
      </div>

      <form
        onSubmit={prepareDocument}
        style={{
          background: '#fff',
          border:
            `1px solid ${colours.border}`,
          borderRadius: 12,
          padding: 18,
          marginBottom: 20
        }}
      >
        <div
          style={{
            color: colours.green,
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.07em',
            marginBottom: 12
          }}
        >
          Prepare a staff document
        </div>

        <div className="form-row">
          <div
            className="form-group"
            style={{
              flex: 1
            }}
          >
            <label className="form-label">
              Steward
            </label>

            <select
              className="form-control"
              value={selectedStaff}
              onChange={event =>
                setSelectedStaff(
                  event.target.value
                )
              }
            >
              <option value="">
                Select steward
              </option>

              {activeStaff.map(record => (
                <option
                  key={record.id}
                  value={record.id}
                >
                  {record.name}
                  {record.staff_id
                    ? ` | ${record.staff_id}`
                    : ''}
                </option>
              ))}
            </select>
          </div>

          <div
            className="form-group"
            style={{
              flex: 2
            }}
          >
            <label className="form-label">
              Document template
            </label>

            <select
              className="form-control"
              value={selectedTemplate}
              onChange={event => {
                setSelectedTemplate(
                  event.target.value
                );
                setError('');
                setHrFields({});
              }}
            >
              <option value="">
                Select active template
              </option>

              {eligibleTemplates.map(
                template => (
                  <option
                    key={template.id}
                    value={template.id}
                  >
                    {template.title}
                    {template.is_activation_gate
                      ? ''
                      : ' [Optional]'}
                  </option>
                )
              )}
            </select>
          </div>

          <div
            className="form-group"
            style={{
              alignSelf: 'flex-end'
            }}
          >
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                working === 'prepare'
              }
            >
              {working === 'prepare'
                ? 'Preparing...'
                : 'Prepare'}
            </button>
          </div>
        </div>

        {selectedTemplateRecord && (
          <div
            style={{
              marginTop: 16,
              marginBottom: 16,
              borderTop:
                `1px solid ${colours.border}`,
              paddingTop: 16
            }}
          >
            <div
              style={{
                color: colours.brown,
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 4
              }}
            >
              {selectedTemplateRecord.title}
            </div>

            <div
              style={{
                color: colours.muted,
                fontSize: 11,
                marginBottom: 14
              }}
            >
              Complete the document-specific
              information below. Fields marked
              with * are required.
            </div>

            {selectedInputFields.length === 0 ? (
              <div
                style={{
                  color: colours.muted,
                  fontSize: 11
                }}
              >
                This template has no information
                for HR to enter manually.
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit,minmax(240px,1fr))',
                  gap: 14
                }}
              >
                {selectedInputFields.map(field => {
                  const value =
                    hrFields[field.field_key] || '';

                  const updateValue = event =>
                    setHrFields(current => ({
                      ...current,
                      [field.field_key]:
                        event.target.value
                    }));

                  return (
                    <div
                      className="form-group"
                      key={field.field_key}
                      style={{
                        marginBottom: 0
                      }}
                    >
                      <label className="form-label">
                        {field.field_label}
                        {field.is_required
                          ? ' *'
                          : ' (Optional)'}
                      </label>

                      {field.input_type ===
                       'textarea' ? (
                        <textarea
                          className="form-control"
                          rows={3}
                          value={value}
                          required={field.is_required}
                          onChange={updateValue}
                        />
                      ) : field.input_type ===
                         'select' ? (
                        <select
                          className="form-control"
                          value={value}
                          required={field.is_required}
                          onChange={updateValue}
                        >
                          <option value="">
                            Select an option
                          </option>

                          {(Array.isArray(field.options)
                            ? field.options
                            : []
                          ).map(option => (
                            <option
                              key={option}
                              value={option}
                            >
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="form-control"
                          type={
                            field.input_type === 'date'
                              ? 'date'
                              : field.input_type === 'number'
                                ? 'number'
                                : 'text'
                          }
                          value={value}
                          required={field.is_required}
                          onChange={updateValue}
                        />
                      )}

                      {field.help_text && (
                        <div
                          style={{
                            color: colours.muted,
                            fontSize: 10,
                            marginTop: 4,
                            lineHeight: 1.4
                          }}
                        >
                          {field.help_text}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div
          style={{
            color: colours.muted,
            fontSize: 11
          }}
        >
          Preparing does not send the
          document. Doris must authorize
          delivery separately.
        </div>
      </form>

      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 10
        }}
      >
        <div
          style={{
            color: colours.brown,
            fontSize: 15,
            fontWeight: 700
          }}
        >
          Document queue
        </div>

        <select
          className="form-control"
          value={statusFilter}
          onChange={changeFilter}
          style={{
            width: 190
          }}
        >
          <option value="">
            All statuses
          </option>

          {Object.keys(statusStyle).map(
            status => (
              <option
                key={status}
                value={status}
              >
                {readableStatus(status)}
              </option>
            )
          )}
        </select>
      </div>

      {documents.length === 0 ? (
        <div className="empty-state">
          <h3>No documents found</h3>
          <p>
            Prepared documents will appear
            here for authorization and
            tracking.
          </p>
        </div>
      ) : (
        <div
          style={{
            background: '#fff',
            border:
              `1px solid ${colours.border}`,
            borderRadius: 12,
            overflowX: 'auto'
          }}
        >
          <table
            style={{
              width: '100%',
              minWidth: 980,
              borderCollapse: 'collapse',
              fontSize: 12
            }}
          >
            <thead>
              <tr
                style={{
                  background:
                    colours.brown
                }}
              >
                {[
                  'Reference',
                  'Steward',
                  'Document',
                  'Status',
                  'Prepared',
                  'Delivery',
                  'Actions'
                ].map(label => (
                  <th
                    key={label}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      color: '#E8C466',
                      fontSize: 9,
                      letterSpacing:
                        '.07em',
                      textTransform:
                        'uppercase'
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {documents.map(
                (record, index) => (
                  <tr
                    key={record.id}
                    style={{
                      background:
                        index % 2 === 0
                          ? '#fff'
                          : '#F9F8F5'
                    }}
                  >
                    <td
                      style={{
                        padding: 12,
                        borderBottom:
                          `1px solid ${colours.border}`,
                        fontFamily:
                          'monospace',
                        fontSize: 10
                      }}
                    >
                      {record.document_ref}
                    </td>

                    <td
                      style={{
                        padding: 12,
                        borderBottom:
                          `1px solid ${colours.border}`
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          color:
                            colours.brown
                        }}
                      >
                        {record.recipient_name}
                      </div>

                      <div
                        style={{
                          color:
                            colours.muted,
                          fontSize: 10
                        }}
                      >
                        {record.recipient_email}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: 12,
                        borderBottom:
                          `1px solid ${colours.border}`,
                        maxWidth: 260
                      }}
                    >
                      {record.template_title}
                    </td>

                    <td
                      style={{
                        padding: 12,
                        borderBottom:
                          `1px solid ${colours.border}`
                      }}
                    >
                      <StatusBadge
                        status={record.status}
                      />
                    </td>

                    <td
                      style={{
                        padding: 12,
                        borderBottom:
                          `1px solid ${colours.border}`
                      }}
                    >
                      <div>
                        {formatDate(
                          record.prepared_at
                        )}
                      </div>

                      <div
                        style={{
                          color:
                            colours.muted,
                          fontSize: 10
                        }}
                      >
                        {record.prepared_by_name ||
                          'System'}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: 12,
                        borderBottom:
                          `1px solid ${colours.border}`
                      }}
                    >
                      Attempts:{' '}
                      {record.delivery_attempts ||
                        0}

                      {record.has_delivery_error && (
                        <div
                          style={{
                            color:
                              colours.red,
                            fontSize: 10
                          }}
                        >
                          Delivery needs review
                        </div>
                      )}
                    </td>

                    <td
                      style={{
                        padding: 12,
                        borderBottom:
                          `1px solid ${colours.border}`
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          flexWrap: 'wrap'
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() =>
                            openDocument(record)
                          }
                          disabled={
                            working ===
                            record.id
                          }
                        >
                          View
                        </button>

                        {record.status ===
                          'ready' &&
                          canAuthorize && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() =>
                                authorizeDocument(
                                  record
                                )
                              }
                              disabled={
                                working ===
                                record.id
                              }
                            >
                              Authorize & Send
                            </button>
                          )}

                        {canRevoke(record) && (
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            style={{
                              color:
                                colours.red,
                              borderColor:
                                colours.red
                            }}
                            onClick={() =>
                              revokeDocument(
                                record
                              )
                            }
                            disabled={
                              working ===
                              record.id
                            }
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedDocument && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{
              maxWidth: 900,
              width: '95vw'
            }}
          >
            <div className="modal-hdr">
              <span className="modal-title">
                {selectedDocument
                  .template_title ||
                  'Document details'}
              </span>

              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  setSelectedDocument(null)
                }
              >
                X
              </button>
            </div>

            <div
              className="modal-body"
              style={{
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 15
                }}
              >
                <StatusBadge
                  status={
                    selectedDocument.status
                  }
                />

                <span
                  style={{
                    color:
                      colours.muted,
                    fontFamily:
                      'monospace',
                    fontSize: 11
                  }}
                >
                  {selectedDocument
                    .document_ref}
                </span>
              </div>

              <div
                style={{
                  background: '#fff',
                  border:
                    `1px solid ${colours.border}`,
                  borderRadius: 8,
                  padding: 25,
                  color: colours.brown,
                  fontFamily:
                    'Georgia, serif',
                  fontSize: 14,
                  lineHeight: 1.8
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    selectedDocument
                      .content_snapshot || ''
                }}
              />
            </div>

            <div className="modal-ftr">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() =>
                  setSelectedDocument(null)
                }
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
